// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Plus, Copy, Calendar, TrendingUp, ChevronDown, Users } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { useAffiliateCoupons } from '@/hooks/useAffiliateCoupons';
import { useAffiliateCouponConversions } from '@/hooks/useAffiliateCouponConversions';
import { CreateCouponRequest } from '@/types/affiliateCoupons';
import { toast } from 'sonner';

interface CouponFormData {
  code: string;
  discount_type: 'percentage' | 'fixed';
  discount_value: string;
  usage_limit: string;
  valid_from: string;
  valid_until: string;
  applies_to_type: string;
}

const AffiliateCouponCodesTab = () => {
  const { profile } = useAuth();
  const [affiliateId, setAffiliateId] = useState<string>('');
  const { coupons, loading, createCoupon } = useAffiliateCoupons(affiliateId);
  const { conversions, loading: conversionsLoading } = useAffiliateCouponConversions(profile?.id || '');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [expandedCoupons, setExpandedCoupons] = useState<Set<string>>(new Set());
  
  // Show login prompt if user is not authenticated
  if (!profile?.id) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Authentication Required</h3>
          <p className="text-gray-600 mb-4">Please log in as an affiliate to view coupon data.</p>
          <p className="text-sm text-gray-500">Log in as: nancy+aff@scoutforce.com</p>
        </div>
      </div>
    );
  }
  
  const [formData, setFormData] = useState<CouponFormData>({
    code: '',
    discount_type: 'percentage',
    discount_value: '',
    usage_limit: '',
    valid_from: new Date().toISOString().split('T')[0],
    valid_until: '',
    applies_to_type: 'membership_first_month'
  });

  // Get affiliate ID on component mount
  useEffect(() => {
    const fetchAffiliateId = async () => {
      if (!profile?.id) {
        console.log('No profile ID available:', profile);
        return;
      }
      
      console.log('Fetching affiliate ID for profile:', profile.id);

      try {
        const { data, error } = await supabase
          .from('affiliates')
          .select('id')
          .eq('user_id', profile.id)
          .maybeSingle();

        console.log('Affiliate fetch result:', { data, error, profileId: profile.id });

        if (error) {
          console.error('Error fetching affiliate ID:', error);
          toast.error('Error loading affiliate data');
          return;
        }

        if (data) {
          console.log('Setting affiliate ID:', data.id);
          setAffiliateId(data.id);
        } else {
          console.log('No affiliate record found for user');
          toast.error('No affiliate account found. Please contact support to set up your affiliate account.');
        }
      } catch (err) {
        console.error('Error in fetchAffiliateId:', err);
        toast.error('Error loading affiliate data');
      }
    };

    fetchAffiliateId();
  }, [profile?.id]);

  const generateCouponCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 8; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData({ ...formData, code: result });
  };

  const handleCreateCoupon = async () => {
    if (!formData.code || !formData.discount_value || !affiliateId) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate 20% maximum discount
    const discountValue = parseFloat(formData.discount_value);
    if (formData.discount_type === 'percentage' && discountValue > 20) {
      toast.error('Maximum discount is 20%');
      return;
    }

    const couponData: CreateCouponRequest = {
      affiliate_id: affiliateId,
      code: formData.code.toUpperCase(),
      discount_type: formData.discount_type,
      discount_value: Math.min(discountValue, 20), // Cap at 20%
      usage_limit: formData.usage_limit ? parseInt(formData.usage_limit) : null,
      valid_from: formData.valid_from,
      valid_until: formData.valid_until || null,
      is_active: true,
      max_discount_percentage: 20,
      applies_to_type: formData.applies_to_type,
      terms_text: 'Valid for 20% off first month of membership only. Cannot be combined with other offers. Affiliate links can still be used for events and other purchases.'
    };

    const success = await createCoupon(couponData);
    if (success) {
      setIsCreateDialogOpen(false);
      setFormData({
        code: '',
        discount_type: 'percentage',
        discount_value: '',
        usage_limit: '',
        valid_from: new Date().toISOString().split('T')[0],
        valid_until: '',
        applies_to_type: 'membership_first_month'
      });
    }
  };

  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    toast.success('Coupon code copied to clipboard');
  };

  const getStatusBadge = (coupon: any) => {
    if (!coupon.is_active) {
      return <Badge variant="secondary">Inactive</Badge>;
    }
    
    const now = new Date();
    const validUntil = coupon.valid_until ? new Date(coupon.valid_until) : null;
    
    if (validUntil && validUntil < now) {
      return <Badge variant="destructive">Expired</Badge>;
    }
    
    if (coupon.usage_limit && coupon.usage_count >= coupon.usage_limit) {
      return <Badge variant="outline">Limit Reached</Badge>;
    }
    
    return <Badge variant="default">Active</Badge>;
  };

  const formatDiscountValue = (type: string, value: number) => {
    return type === 'percentage' ? `${value}%` : `$${value.toFixed(2)}`;
  };

  const calculateTotalSavings = () => {
    return coupons.reduce((total, coupon) => {
      // Estimate average order value for percentage discounts
      const avgOrderValue = 50; // This should ideally come from actual data
      let savings = 0;
      
      if (coupon.discount_type === 'percentage') {
        savings = (avgOrderValue * coupon.discount_value / 100) * coupon.usage_count;
      } else {
        savings = coupon.discount_value * coupon.usage_count;
      }
      
      return total + savings;
    }, 0);
  };

  const toggleCouponExpansion = (couponCode: string) => {
    const newExpanded = new Set(expandedCoupons);
    if (newExpanded.has(couponCode)) {
      newExpanded.delete(couponCode);
    } else {
      newExpanded.add(couponCode);
    }
    setExpandedCoupons(newExpanded);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center">Loading coupon codes...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Coupons</CardTitle>
            <Copy className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{coupons.length}</div>
            <p className="text-xs text-muted-foreground">
              {coupons.filter(c => c.is_active).length} active
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Uses</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreference" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {coupons.reduce((sum, c) => sum + c.usage_count, 0)}
            </div>
            <p className="text-xs text-muted-foreground">
              Across all coupons
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Customer Savings</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              ${calculateTotalSavings().toFixed(2)}
            </div>
            <p className="text-xs text-muted-foreground">
              Total savings provided
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Coupon Codes Table */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>Your Coupon Codes</CardTitle>
              <CardDescription>
                Create and manage discount codes to share with your audience
              </CardDescription>
            </div>
            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Coupon
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Create New Coupon Code</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="code">Coupon Code</Label>
                    <div className="flex gap-2">
                      <Input
                        id="code"
                        value={formData.code}
                        onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                        placeholder="Enter code or generate"
                      />
                      <Button variant="outline" onClick={generateCouponCode}>
                        Generate
                      </Button>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="discount_type">Discount Type</Label>
                    <Select 
                      value={formData.discount_type} 
                      onValueChange={(value: 'percentage' | 'fixed') => 
                        setFormData({ ...formData, discount_type: value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percentage">Percentage</SelectItem>
                        <SelectItem value="fixed">Fixed Amount</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="applies_to_type">Applies To</Label>
                    <Select 
                      value={formData.applies_to_type} 
                      onValueChange={(value) => 
                        setFormData({ ...formData, applies_to_type: value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="membership_first_month">First month of membership only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="discount_value">
                      Discount Value (% - Max 20%)
                    </Label>
                    <Input
                      id="discount_value"
                      type="number"
                      max="20"
                      value={formData.discount_value}
                      onChange={(e) => {
                        const value = e.target.value;
                        const numValue = parseFloat(value);
                        if (numValue > 20) {
                          toast.error('Maximum discount is 20%');
                          setFormData({ ...formData, discount_value: '20' });
                        } else {
                          setFormData({ ...formData, discount_value: value });
                        }
                      }}
                      placeholder="10"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Auto-generates terms: "Valid for 20% off first month of membership only"
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="usage_limit">Usage Limit (Optional)</Label>
                    <Input
                      id="usage_limit"
                      type="number"
                      value={formData.usage_limit}
                      onChange={(e) => setFormData({ ...formData, usage_limit: e.target.value })}
                      placeholder="Leave empty for unlimited"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="valid_from">Valid From</Label>
                      <Input
                        id="valid_from"
                        type="date"
                        value={formData.valid_from}
                        onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="valid_until">Valid Until (Optional)</Label>
                      <Input
                        id="valid_until"
                        type="date"
                        value={formData.valid_until}
                        onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button onClick={handleCreateCoupon}>
                      Create Coupon
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {coupons.length === 0 ? (
            <div className="text-center py-8">
              <Copy className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Coupon Codes Yet</h3>
              <p className="text-gray-600 mb-4">Create your first coupon code to start offering discounts to your audience</p>
              <Button onClick={() => setIsCreateDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Your First Coupon
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Discount</TableHead>
                  <TableHead>Terms</TableHead>
                  <TableHead>Usage</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {coupons.map((coupon) => {
                  const couponConversions = conversions[coupon.code] || [];
                  const isExpanded = expandedCoupons.has(coupon.code);
                  
                  return (
                     <React.Fragment key={coupon.id}>
                       <TableRow>
                         <TableCell className="font-mono font-medium">
                           <div className="flex items-center gap-2">
                             <span className="text-sm">{coupon.code} - {formatDiscountValue(coupon.discount_type, coupon.discount_value)} off first month</span>
                             <Button
                               variant="ghost"
                               size="sm"
                               onClick={() => handleCopyCode(coupon.code)}
                               className="h-6 w-6 p-0"
                             >
                               <Copy className="h-3 w-3" />
                             </Button>
                             <Button
                               variant="ghost"
                               size="sm"
                               onClick={() => toggleCouponExpansion(coupon.code)}
                               className="h-6 w-6 p-0"
                               aria-label={isExpanded ? "Hide usage details" : "Show usage details"}
                             >
                               <ChevronDown className={`h-3 w-3 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                             </Button>
                           </div>
                           <div className="text-xs text-gray-500 mt-1">
                             Membership only. Events use referral link.
                           </div>
                         </TableCell>
                        <TableCell>
                          <div className="text-sm font-medium">
                            {formatDiscountValue(coupon.discount_type, coupon.discount_value)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <div className="text-xs text-green-600 flex items-center gap-1">
                              <span>✓</span>
                              <span>20% off first month of membership</span>
                            </div>
                            <div className="text-xs text-green-600 flex items-center gap-1">
                              <span>✓</span>
                              <span>Your referral link still earns commission on events</span>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          {coupon.usage_count}
                          {coupon.usage_limit && ` / ${coupon.usage_limit}`}
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(coupon)}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleCopyCode(coupon.code)}
                            >
                              <Copy className="h-3 w-3 mr-1" />
                              Copy
                            </Button>
                            {couponConversions.length > 0 && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => toggleCouponExpansion(coupon.code)}
                                className="text-xs"
                              >
                                <Users className="h-3 w-3 mr-1" />
                                View Users ({couponConversions.length})
                                <ChevronDown className={`h-3 w-3 ml-1 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                      
                      {isExpanded && couponConversions.length > 0 && (
                        <TableRow>
                          <TableCell colSpan={6} className="p-0">
                            <div className="bg-muted/50 p-4 border-t">
                              <h4 className="font-medium text-sm mb-3 flex items-center gap-2">
                                <Users className="h-4 w-4" />
                                Recent Users ({couponConversions.length})
                              </h4>
                              <div className="space-y-2">
                                {couponConversions.slice(0, 10).map((conversion) => (
                                  <div key={conversion.id} className="flex items-center gap-3 py-2 px-3 bg-background rounded-md">
                                    <Avatar className="h-8 w-8">
                                      <AvatarImage src={conversion.user_avatar || undefined} />
                                      <AvatarFallback className="text-xs">
                                        {conversion.user_name.split(' ').map(n => n[0]).join('').toUpperCase()}
                                      </AvatarFallback>
                                    </Avatar>
                                    <div className="flex-1 min-w-0">
                                      <div className="text-sm font-medium truncate">
                                        {conversion.user_name}
                                      </div>
                                      <div className="text-xs text-muted-foreground truncate">
                                        {conversion.user_email}
                                      </div>
                                    </div>
                                    <div className="text-right">
                                      <div className="text-sm font-medium text-green-600">
                                        ${conversion.amount_earned.toFixed(2)} saved
                                      </div>
                                      <div className="text-xs text-muted-foreground">
                                        {formatDate(conversion.earned_at)}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                                {couponConversions.length > 10 && (
                                  <div className="text-xs text-muted-foreground text-center py-2">
                                    And {couponConversions.length - 10} more users...
                                  </div>
                                )}
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AffiliateCouponCodesTab;